import React, { useEffect, useRef, useState } from "react";

export default function TeamPanel({ title = "Our Team", folder = "/about/team" }) {
	const panelsRef = useRef(null);
	// Active real index (0..n-1)
	const [active, setActive] = useState(0);
	const extendedIndexRef = useRef(0); // will be set after initial positioning
	const isAdjustingRef = useRef(false);

	// Base images
	const baseImages = ["1.jpg", "2.jpg", "3.jpg", "4.jpg"].map((n) => `${folder}/${n}`);
	const CLONE_COUNT = baseImages.length; // clone entire set on each side for maximum seamlessness
	// Extended with two (or fewer if not enough) clones at both ends
	const images = [
		...baseImages.slice(-CLONE_COUNT),
		...baseImages,
		...baseImages.slice(0, CLONE_COUNT)
	];

	useEffect(() => {
		const el = panelsRef.current;
		if (!el) return;
		const getPanelWidth = () => {
			const firstReal =
				el.querySelector(`.home-team-panel[data-extended-index="${CLONE_COUNT}"]`) ||
				el.querySelector(".home-team-panel");
			return firstReal ? firstReal.getBoundingClientRect().width : el.clientWidth || 1;
		};

		// Position at first real panel (offset by CLONE_COUNT clones at the start)
		requestAnimationFrame(() => {
			el.scrollTo({ left: getPanelWidth() * CLONE_COUNT, behavior: "auto" });
			extendedIndexRef.current = CLONE_COUNT;
		});

		function withSnapDisabled(fn) {
			const prev = el.style.scrollSnapType;
			el.style.scrollSnapType = "none";
			fn();
			// force reflow before restoring snap to avoid flicker
			void el.offsetHeight; // reflow
			el.style.scrollSnapType = prev || "";
		}

		function normalize(rawIdx) {
			const panelWidth = getPanelWidth();
			const total = images.length;
			const firstReal = CLONE_COUNT;
			const lastRealStart = CLONE_COUNT + baseImages.length - 1;
			if (rawIdx < firstReal) {
				// Jump forward to matching real index position
				const adjusted = rawIdx + baseImages.length;
				isAdjustingRef.current = true;
				withSnapDisabled(() => {
					el.scrollTo({ left: panelWidth * adjusted, behavior: "auto" });
				});
				extendedIndexRef.current = adjusted;
				const realIdx = (adjusted - CLONE_COUNT) % baseImages.length;
				setActive(realIdx);
				isAdjustingRef.current = false;
			} else if (rawIdx > lastRealStart) {
				// Jump backward wrapping around
				const adjusted = rawIdx - baseImages.length;
				isAdjustingRef.current = true;
				withSnapDisabled(() => {
					el.scrollTo({ left: panelWidth * adjusted, behavior: "auto" });
				});
				extendedIndexRef.current = adjusted;
				const realIdx = (adjusted - CLONE_COUNT + baseImages.length) % baseImages.length;
				setActive(realIdx);
				isAdjustingRef.current = false;
			}
		}

		function onScroll() {
			if (isAdjustingRef.current) return;
			const panelWidth = getPanelWidth();
			const rawIdx = Math.round(el.scrollLeft / panelWidth);
			extendedIndexRef.current = rawIdx;
			const realIdx =
				(((rawIdx - CLONE_COUNT) % baseImages.length) + baseImages.length) % baseImages.length;
			setActive(realIdx);
			if (rawIdx < CLONE_COUNT || rawIdx >= CLONE_COUNT + baseImages.length) {
				requestAnimationFrame(() => normalize(rawIdx));
			}
		}

		el.addEventListener("scroll", onScroll, { passive: true });
		window.addEventListener("resize", onScroll);
		return () => {
			el.removeEventListener("scroll", onScroll);
			window.removeEventListener("resize", onScroll);
		};
	}, [baseImages.length]);

	function scrollToExtended(extIdx, behavior = "smooth") {
		const el = panelsRef.current;
		if (!el) return;
		const target = el.querySelector(`.home-team-panel[data-extended-index="${extIdx}"]`);
		if (!target) return;
		el.scrollTo({ left: target.offsetLeft, behavior });
	}

	function scrollToReal(realIdx) {
		const targetExt = realIdx + CLONE_COUNT; // offset due to leading clones
		extendedIndexRef.current = targetExt;
		scrollToExtended(targetExt, "smooth");
	}

	function prev() {
		scrollToExtended(extendedIndexRef.current - 1);
	}

	function next() {
		scrollToExtended(extendedIndexRef.current + 1);
	}

	// Autoplay / interval rotation
	useEffect(() => {
		const AUTOPLAY_MS = 4500; // rotation interval
		let timerId;
		function tick() {
			if (document.hidden) return; // don't rotate in background tab
			if (isAdjustingRef.current) return; // avoid during normalization jump
			next();
		}
		timerId = setInterval(tick, AUTOPLAY_MS);
		return () => clearInterval(timerId);
	}, []);

	return (
		<div className="home-team-root">
			<div className="home-team-group">
				<div className="home-team-controls">
					<button
						className="home-team-arrow home-team-arrow--left"
						onClick={prev}
						aria-label="Previous"
					>
						<img src="/about/left.png" alt="Previous" width="22" height="22" />
					</button>
					<div
						ref={panelsRef}
						className="home-team-panels"
						role="region"
						aria-labelledby="team-bio-heading"
					>
						{images.map((src, i) => {
							const realIdx =
								(((i - CLONE_COUNT) % baseImages.length) + baseImages.length) % baseImages.length;
							return (
								<div key={i} className="home-team-panel" data-extended-index={i}>
									<section>
										<img src={src} alt={`Team ${realIdx + 1}`} className="home-team-thumb" />
									</section>
								</div>
							);
						})}
					</div>
					<button
						className="home-team-arrow home-team-arrow--right"
						onClick={next}
						aria-label="Next"
					>
						<img src="/about/right.png" alt="Next" width="22" height="22" />
					</button>
				</div>
				<div className="home-team-pagination" role="tablist" aria-label="Team photos pagination">
					{baseImages.map((_, realIdx) => (
						<button
							key={realIdx}
							className={"home-team-dot" + (active === realIdx ? " active" : "")}
							aria-label={`Show team photo ${realIdx + 1}`}
							aria-pressed={active === realIdx}
							onClick={() => scrollToReal(realIdx)}
						/>
					))}
				</div>
			</div>
		</div>
	);
}
